import { type FC } from "react"

import { toDateTimeString } from "@forento/core/utilities/date"
import Tooltip from "@forento/ui/components/Tooltip"

import { DeleteIcon } from "~/components/Icon"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const BannedIpsSettingsPage: FC = () => {
	const alert = useAlert()

	const { data, error, refetch } = query.platform.listBannedIps.useQuery()

	async function handleDelete(entry: { id: number; ip: string }) {
		const result = await alert.confirm(
			"Delete banned IP",
			`Are you sure you want to delete the banned IP **${entry.ip}**?`,
		)
		if (!result.result) return

		try {
			await trpc.platform.deleteBannedIp.mutate(entry.id)
			await refetch()
			result.close()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to delete banned IP. Please try again later.")
		}
	}

	return (
		<Layout>
			<PageBreadcrumb title="Banned IPs" path={[{ title: "Settings", link: routes.settings.index() }]} />
			<p className="mb-6">When deleting users you can choose to ban their IPs as well. These will appear here.</p>
			{error ? (
				<p>Failed to load banned IPs.</p>
			) : data === undefined ? (
				<PartialLoadingPage />
			) : (
				<Table
					emptyText="No IP addresses are banned."
					columns={["IP", "Ban date", "Actions"]}
					data={data.map(x => [
						x.ip,
						toDateTimeString(x.createDate),
						<div className="flex">
							<Tooltip tooltip="Delete" className="flex">
								<button type="button" onClick={() => handleDelete(x)}>
									<DeleteIcon className="text-danger size-6" />
								</button>
							</Tooltip>
						</div>,
					])}
				/>
			)}
		</Layout>
	)
}

export default BannedIpsSettingsPage
